import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigationType } from "react-router";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import PullToRefresh from "react-simple-pull-to-refresh";
// layouts
import { MainLayout } from "layouts";

// components
import { HomeContents } from "components/home";
import { Avatar, NotificationIcon } from "components/common";
import PopupBranch from "components/ui/popup/Branch";
import Text from "components/ui/Text";
import PullToRefreshing from "components/common/PullToRefreshing";

// hooks
import useDidMountEffect from "hooks/useDidMountEffect";

// assets
import BodycodiSymbol from "assets/image/bodycodi_symbol_white_s.png";

// redux
import { fetchHomeData, setFromTop } from "redux/slices/home/homeSlice";
import { managerPaymentKeys } from "queries/Sales/useManagerPaymentQuery";
import { showCeoReportModal } from "../../components/report/ceo-report-modal";

const Home = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigationType = useNavigationType();
  const { seqPartner, seqPartnerBranch } = useSelector(
    (state) => state.partner
  );
  const { name } = useSelector((state) => state.user.userInfo);
  const { fromTop } = useSelector((state) => state.home);
  const pageRef = useRef();
  const { userInfo } = useSelector((state) => state.user);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const isAlreadyVisit = window.localStorage.getItem(
        "isFirstVisitForCEOReport"
      );
      if (!isAlreadyVisit) {
        showCeoReportModal();
        window.localStorage.setItem("isFirstVisitForCEOReport", "true");
      }
    }
  }, []);

  const HeaderComponents = {
    leftComponent: () => {
      return (
        <HeaderWrapper>
          <Symbol src={BodycodiSymbol} />
          <div className="divider" />
          <Text
            color="#FFFFFF"
            fontSize="18px"
            lineHeight="32px"
            fontWeight="bold"
          >
            {`안녕하세요! ${name ? name + "님" : ""}`}
          </Text>
        </HeaderWrapper>
      );
    },

    rightComponent: () => {
      return (
        <RightWrap>
          <NotificationIcon mode="light" />
          <Link to="/all">
            <Avatar src={userInfo.imageUrl} size={32} minSize={false} />
          </Link>
        </RightWrap>
      );
    },

    bottomComponent: () => {
      return (
        <BranchSelectWrapper>
          <PopupBranch color="white" isHome={true} isAll={true} hideFoldIcon />
        </BranchSelectWrapper>
      );
    },
  };

  useLayoutEffect(() => {
    if (navigationType === "PUSH") {
      dispatch(fetchHomeData());
    } else {
      if (fromTop > 0) {
        pageRef.current.scrollTo({
          top: fromTop,
        });
      }
    }

    return () => {
      if (!!pageRef.current) {
        dispatch(setFromTop(pageRef.current.scrollTop));
      }
    };
  }, []);

  useDidMountEffect(() => {
    dispatch(fetchHomeData());
  }, [seqPartner, seqPartnerBranch]);

  const handleRefresh = () => {
    return new Promise((res) => {
      setTimeout(() => {
        Promise.all([
          dispatch(fetchHomeData()),
          queryClient.invalidateQueries({ queryKey: managerPaymentKeys.all }),
        ]).then(() => {
          res();
        });
      }, 700);
    });
  };

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      resistance={2}
      pullingContent={<></>}
      refreshingContent={<PullToRefreshing />}
    >
      <MainLayout
        withHeader={true}
        withBottomTab={true}
        headerProps={{
          withBackButton: false,
          isCenter: false,
          backgroundColor: "#0F2859",
          padding: "14px 24px 14px 24px",
          leftComponent: HeaderComponents.leftComponent,
          rightComponent: HeaderComponents.rightComponent,
          bottomComponent: HeaderComponents.bottomComponent,
        }}
        withPartner={true}
      >
        <Wrapper id="home-contents" ref={pageRef}>
          <HomeContents />
        </Wrapper>
      </MainLayout>
    </PullToRefresh>
  );
};

const Wrapper = styled.div`
  position: absolute;
  width: 100%;
  height: calc(100% - 190px);
  overflow-y: scroll;
  padding: 0 16px 16px 16px;
  margin-top: 70px;
  background: -moz-linear-gradient(top, #0f2859 48px, #f3f7ff 48px);
  background: -webkit-linear-gradient(top, #0f2859 48px, #f3f7ff 48px);
`;

const BranchSelectWrapper = styled.div`
  background-color: #0f2859;
  padding: 0px 16px 16px 16px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  div.divider {
    border-left: 1px solid rgba(255, 255, 255, 0.5);
    height: 20px;
    margin-right: 10px;
    margin-left: 10px;
  }
`;

const RightWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

const Symbol = styled.img`
  width: 18px;
  height: 22px;
`;

export default Home;
